import React, { useState, useEffect } from 'react'
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Tabs,
  Timeline,
  Empty,
  Select,
  message,
  Divider,
  Modal,
  Form,
  Input,
  DatePicker,
  Popconfirm
} from 'antd'
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  UserAddOutlined,
  PlusOutlined,
  DeleteOutlined,
  MessageOutlined
} from '@ant-design/icons'
import { Lead, AdminUser, leadsApi, NurturingTimelineItem, nurturingApiService, CreateNurturingTaskData } from '../services/api'
import dayjs from 'dayjs'

const { TabPane } = Tabs
const { Option } = Select
const { TextArea } = Input

interface LeadDetailProps {
  lead: Lead
  adminUsers: AdminUser[]
  onUpdate: () => void
  onAssign: (userId?: string) => void
  onConvert: () => void
}

const LeadDetail: React.FC<LeadDetailProps> = ({
  lead,
  adminUsers,
  onUpdate,
  onAssign,
  onConvert
}) => {
  const [nurturingTimeline, setNurturingTimeline] = useState<NurturingTimelineItem[]>([])
  const [loadingTimeline, setLoadingTimeline] = useState(false)
  const [assigning, setAssigning] = useState(false)
  const [createTaskModalVisible, setCreateTaskModalVisible] = useState(false)
  const [taskForm] = Form.useForm()
  const [updatingTask, setUpdatingTask] = useState<string | null>(null)
  const [editNotesModalVisible, setEditNotesModalVisible] = useState(false)
  const [notesForm] = Form.useForm()
  const [updatingNotes, setUpdatingNotes] = useState(false)

  useEffect(() => {
    fetchNurturingTimeline()
  }, [lead.id])

  const fetchNurturingTimeline = async () => {
    setLoadingTimeline(true)
    try {
      const response = await leadsApi.getNurturingTimeline(lead.id)
      setNurturingTimeline(response.data)
    } catch (error) {
      console.error('Failed to fetch nurturing timeline:', error)
    } finally {
      setLoadingTimeline(false)
    }
  }

  const handleAssign = async (userId?: string) => {
    setAssigning(true)
    try {
      await onAssign(userId)
      message.success('Lead assigned successfully')
    } catch (error) {
      message.error('Failed to assign lead')
    } finally {
      setAssigning(false)
    }
  }

  const handleCreateTask = async (values: any) => {
    try {
      const taskData: CreateNurturingTaskData = {
        taskType: values.taskType,
        scheduledDate: values.scheduledDate.toISOString(),
        notes: values.notes,
        metadata: {}
      }
      await nurturingApiService.createTask(lead.id, taskData)
      message.success('Nurturing task created successfully')
      setCreateTaskModalVisible(false)
      taskForm.resetFields()
      fetchNurturingTimeline()
    } catch (error: any) {
      console.error('Failed to create task:', error)
      message.error(error.response?.data?.message || 'Failed to create nurturing task')
    }
  }

  const handleUpdateTaskStatus = async (taskId: string, status: 'completed' | 'skipped' | 'cancelled') => {
    setUpdatingTask(taskId)
    try {
      await nurturingApiService.updateTask(lead.id, taskId, {
        status,
        completedDate: status === 'completed' ? new Date().toISOString() : undefined
      })
      message.success(`Task marked as ${status}`)
      fetchNurturingTimeline()
    } catch (error: any) {
      console.error('Failed to update task:', error)
      message.error('Failed to update task status')
    } finally {
      setUpdatingTask(null)
    }
  }

  const handleDeleteTask = async (taskId: string) => {
    try {
      await nurturingApiService.deleteTask(lead.id, taskId)
      message.success('Task deleted successfully')
      fetchNurturingTimeline()
    } catch (error: any) {
      console.error('Failed to delete task:', error)
      message.error('Failed to delete task')
    }
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      new: 'blue',
      contacted: 'cyan',
      qualified: 'green',
      nurturing: 'purple',
      demo_scheduled: 'orange',
      onboarding: 'gold',
      converted: 'success',
      lost: 'red'
    }
    return colors[status] || 'default'
  }

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'green',
      medium: 'blue',
      high: 'orange',
      urgent: 'red'
    }
    return colors[priority] || 'default'
  }

  const getTaskStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'blue',
      completed: 'green',
      skipped: 'orange',
      cancelled: 'red'
    }
    return colors[status] || 'default'
  }

  return (
    <div>
      {/* Quick Actions */}
      <Card style={{ marginBottom: '16px' }}>
        <Space>
          <Select
            placeholder="Assign to..."
            value={lead.assignedTo || undefined}
            onChange={handleAssign}
            loading={assigning}
            style={{ width: 200 }}
            allowClear
          >
            {adminUsers.map(user => (
              <Option key={user.id} value={user.id}>
                {user.firstName} {user.lastName}
              </Option>
            ))}
          </Select>
          {lead.status !== 'converted' && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={onConvert}
            >
              Convert to Business
            </Button>
          )}
        </Space>
      </Card>

      <Tabs defaultActiveKey="overview">
        <TabPane tab="Overview" key="overview">
          <Card title="Contact Information" style={{ marginBottom: '16px' }}>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Name">
                {lead.firstName || lead.lastName
                  ? `${lead.firstName || ''} ${lead.lastName || ''}`.trim()
                  : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                <Space>
                  <MailOutlined />
                  {lead.email}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {lead.phone ? (
                  <Space>
                    <PhoneOutlined />
                    {lead.phone}
                  </Space>
                ) : (
                  'N/A'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(lead.status)}>
                  {lead.status.replace('_', ' ')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Priority">
                <Tag color={getPriorityColor(lead.priority)}>{lead.priority}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Assigned To">
                {lead.assignedUser ? (
                  <Space>
                    <UserOutlined />
                    {lead.assignedUser.firstName} {lead.assignedUser.lastName}
                  </Space>
                ) : (
                  <span style={{ color: '#999' }}>Unassigned</span>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Source">
                {lead.source || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Lead Source">
                {lead.leadSource || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Service Area">
                {lead.serviceArea || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Website">
                {lead.websiteUrl ? (
                  <a href={lead.websiteUrl} target="_blank" rel="noopener noreferrer">
                    {lead.websiteUrl}
                  </a>
                ) : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Google Rating">
                {lead.googleStarRating != null ? (
                  <Space>
                    <span style={{ color: '#fadb14' }}>★</span>
                    {Number(lead.googleStarRating).toFixed(1)}
                    {lead.googleReviewCount != null && (
                      <span style={{ color: '#999' }}>({lead.googleReviewCount} reviews)</span>
                    )}
                  </Space>
                ) : 'N/A'}
              </Descriptions.Item>
              {lead.estimatedValue && (
                <Descriptions.Item label="Estimated Value">
                  <Space>
                    <DollarOutlined />
                    ${lead.estimatedValue.toLocaleString()}
                  </Space>
                </Descriptions.Item>
              )}
              {lead.followUpDate && (
                <Descriptions.Item label="Follow Up Date">
                  <Space>
                    <CalendarOutlined />
                    {new Date(lead.followUpDate).toLocaleDateString()}
                  </Space>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Created">
                {new Date(lead.createdAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {new Date(lead.updatedAt).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card
            title={`People at this company (${1 + (lead.contacts?.length || 0)})`}
            style={{ marginBottom: '16px' }}
          >
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600 }}>
                {[lead.firstName, lead.lastName].filter(Boolean).join(' ') || lead.company || 'Primary'}
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  Primary
                </Tag>
              </div>
              <div style={{ fontSize: 13, color: '#666' }}>
                <MailOutlined style={{ marginRight: 6 }} />
                {lead.email}
              </div>
              {lead.phone && (
                <div style={{ fontSize: 13, color: '#666' }}>
                  <PhoneOutlined style={{ marginRight: 6 }} />
                  {lead.phone}
                </div>
              )}
            </div>
            {lead.contacts && lead.contacts.length > 0 ? (
              lead.contacts.map((person) => {
                const name = [person.firstName, person.lastName].filter(Boolean).join(' ') || 'Unknown'
                return (
                  <div
                    key={person.id}
                    style={{
                      padding: '10px 0',
                      borderTop: '1px solid #f0f0f0',
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>
                      {name}
                      {person.title ? (
                        <span style={{ fontWeight: 400, color: '#666', marginLeft: 8 }}>
                          {person.title}
                        </span>
                      ) : null}
                      <Tag style={{ marginLeft: 8 }}>{person.source}</Tag>
                    </div>
                    <div style={{ fontSize: 13, color: '#666' }}>
                      <MailOutlined style={{ marginRight: 6 }} />
                      <a href={`mailto:${person.email}`}>{person.email}</a>
                    </div>
                  </div>
                )
              })
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No additional people found yet — run Enrich emails after discovery"
              />
            )}
          </Card>

          {lead.message && (
            <Card title="Message" style={{ marginBottom: '16px' }}>
              <p style={{ whiteSpace: 'pre-wrap' }}>{lead.message}</p>
            </Card>
          )}

          <Card 
            title="Internal Notes" 
            style={{ marginBottom: '16px' }}
            extra={
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  notesForm.setFieldsValue({ notes: lead.notes || '' })
                  setEditNotesModalVisible(true)
                }}
              >
                Edit
              </Button>
            }
          >
            {lead.notes ? (
              <p style={{ whiteSpace: 'pre-wrap' }}>{lead.notes}</p>
            ) : (
              <div style={{ color: '#999', fontStyle: 'italic' }}>
                No notes yet. Click Edit to add notes.
              </div>
            )}
          </Card>

          {lead.convertedBusiness && (
            <Card title="Conversion Information" style={{ marginBottom: '16px' }}>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Converted Business">
                  <Space>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    {lead.convertedBusiness.name}
                  </Space>
                </Descriptions.Item>
                {lead.conversionDate && (
                  <Descriptions.Item label="Conversion Date">
                    {new Date(lead.conversionDate).toLocaleString()}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}
        </TabPane>

        <TabPane tab="Nurturing Timeline" key="timeline">
          <Card
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateTaskModalVisible(true)}
              >
                Add Activity
              </Button>
            }
          >
            {loadingTimeline ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
            ) : nurturingTimeline.length === 0 ? (
              <Empty description="No nurturing activities yet">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateTaskModalVisible(true)}
                >
                  Add First Activity
                </Button>
              </Empty>
            ) : (
              <Timeline>
                {nurturingTimeline.map((item) => (
                  <Timeline.Item
                    key={item.id}
                    color={
                      item.status === 'completed'
                        ? 'green'
                        : item.status === 'cancelled'
                        ? 'red'
                        : 'blue'
                    }
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                          {item.taskType.toUpperCase()} -{' '}
                          <Tag color={getTaskStatusColor(item.status)}>
                            {item.status}
                          </Tag>
                        </div>
                        <div style={{ color: '#666', fontSize: '12px', marginBottom: '4px' }}>
                          Scheduled: {new Date(item.scheduledDate).toLocaleString()}
                        </div>
                        {item.completedDate && (
                          <div style={{ color: '#666', fontSize: '12px', marginBottom: '4px' }}>
                            Completed: {new Date(item.completedDate).toLocaleString()}
                          </div>
                        )}
                        {item.notes && (
                          <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                            {item.notes}
                          </div>
                        )}
                      </div>
                      <Space>
                        {item.status === 'pending' && (
                          <>
                            <Button
                              size="small"
                              type="link"
                              onClick={() => handleUpdateTaskStatus(item.id, 'completed')}
                              loading={updatingTask === item.id}
                            >
                              Complete
                            </Button>
                            <Button
                              size="small"
                              type="link"
                              onClick={() => handleUpdateTaskStatus(item.id, 'skipped')}
                              loading={updatingTask === item.id}
                            >
                              Skip
                            </Button>
                          </>
                        )}
                        <Popconfirm
                          title="Delete this task?"
                          onConfirm={() => handleDeleteTask(item.id)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            size="small"
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                          />
                        </Popconfirm>
                      </Space>
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </Card>
        </TabPane>
      </Tabs>

      {/* Create Nurturing Task Modal */}
      <Modal
        title="Add Nurturing Activity"
        open={createTaskModalVisible}
        onCancel={() => {
          setCreateTaskModalVisible(false)
          taskForm.resetFields()
        }}
        footer={null}
        width={600}
      >
        <Form
          form={taskForm}
          layout="vertical"
          onFinish={handleCreateTask}
        >
          <Form.Item
            name="taskType"
            label="Activity Type"
            rules={[{ required: true, message: 'Please select activity type' }]}
            initialValue="note"
          >
            <Select>
              <Option value="note">
                <Space>
                  <MessageOutlined />
                  Note
                </Space>
              </Option>
              <Option value="email">
                <Space>
                  <MailOutlined />
                  Email
                </Space>
              </Option>
              <Option value="call">
                <Space>
                  <PhoneOutlined />
                  Call
                </Space>
              </Option>
              <Option value="sms">
                <Space>
                  <MessageOutlined />
                  SMS
                </Space>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="scheduledDate"
            label="Scheduled Date & Time"
            rules={[{ required: true, message: 'Please select scheduled date' }]}
            initialValue={dayjs()}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm"
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <TextArea
              rows={4}
              placeholder="Add notes about this activity..."
            />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => {
                setCreateTaskModalVisible(false)
                taskForm.resetFields()
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create Activity
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Notes Modal */}
      <Modal
        title="Edit Notes"
        open={editNotesModalVisible}
        onCancel={() => {
          setEditNotesModalVisible(false)
          notesForm.resetFields()
        }}
        onOk={() => {
          notesForm.submit()
        }}
        confirmLoading={updatingNotes}
        width={600}
      >
        <Form
          form={notesForm}
          layout="vertical"
          onFinish={async (values) => {
            setUpdatingNotes(true)
            try {
              await leadsApi.update(lead.id, { notes: values.notes })
              message.success('Notes updated successfully')
              setEditNotesModalVisible(false)
              notesForm.resetFields()
              onUpdate()
            } catch (error: any) {
              console.error('Failed to update notes:', error)
              message.error(error.response?.data?.message || 'Failed to update notes')
            } finally {
              setUpdatingNotes(false)
            }
          }}
        >
          <Form.Item
            name="notes"
            label="Notes"
          >
            <TextArea
              rows={8}
              placeholder="Add internal notes about this lead..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default LeadDetail

